import { z } from 'zod';
import { articleOfferSpecSchema, specRowsToLocalized, type ArticleOfferSpec } from '../inventory/article-offer-spec.schema';
import { listQuerySchema } from '../common/pagination';
import { createFromOfferChecklistSchema } from './offer-checklist';
import {
  computeQuoteCalculation,
  FORMULA_SCRAPER_TYPES,
  SCRAPER_TYPES,
  type QuoteCalcFormulas,
  type QuoteCalcGroupScope,
  type QuoteCalcInput,
  type QuoteCalcResult,
  type FormulaScraperType,
  type ScraperType,
} from './quote-calculation-engine';

/**
 * Angebotskalkulation (ADR 0112): API-Verträge. Geld als Mikro-Einheiten im
 * String (bigint über JSON), Mengen und Prozente als Zahl.
 */

export const scraperTypeSchema = z.enum(SCRAPER_TYPES);

export const quoteCalcStatusSchema = z.enum(['DRAFT', 'SENT', 'WON', 'LOST']);
export type QuoteCalcStatus = z.infer<typeof quoteCalcStatusSchema>;
export const QUOTE_CALC_STATUS_LABELS: Record<QuoteCalcStatus, string> = {
  DRAFT: 'Entwurf',
  SENT: 'Gesendet',
  WON: 'Gewonnen',
  LOST: 'Verloren',
};

export const quoteCalcLanguageSchema = z.enum(['DE', 'EN']);
export type QuoteCalcLanguage = z.infer<typeof quoteCalcLanguageSchema>;

export const QUOTE_CALC_GROUP_KEYS = [
  'PLASTIC',
  'ELECTRICAL',
  'STEEL',
  'ASSEMBLY',
  'DOCUMENTATION',
  'FREIGHT',
  'OPTIONS',
] as const;
export const quoteCalcGroupKeySchema = z.enum(QUOTE_CALC_GROUP_KEYS);
export type QuoteCalcGroupKey = z.infer<typeof quoteCalcGroupKeySchema>;

export const quoteCalcPricingModeSchema = z.enum(['PROGRESSIVE', 'RETROGRADE']);

/** Voreinstellung je Gruppe für eine neue Kalkulation (aus der Kalkulation 26100730). */
export const QUOTE_CALC_GROUP_DEFAULTS: Record<
  QuoteCalcGroupKey,
  { title: string; pricingMode: 'PROGRESSIVE' | 'RETROGRADE'; surchargePct: number; isSplit: boolean }
> = {
  PLASTIC: { title: 'Kunststoffkomponenten', pricingMode: 'RETROGRADE', surchargePct: -30, isSplit: false },
  ELECTRICAL: { title: 'Elektrokomponenten', pricingMode: 'RETROGRADE', surchargePct: 0, isSplit: false },
  STEEL: { title: 'Stahlkomponenten', pricingMode: 'PROGRESSIVE', surchargePct: 35, isSplit: false },
  ASSEMBLY: { title: 'Montage', pricingMode: 'RETROGRADE', surchargePct: 0, isSplit: false },
  DOCUMENTATION: { title: 'Dokumentation / Engineering', pricingMode: 'RETROGRADE', surchargePct: 0, isSplit: true },
  FREIGHT: { title: 'Fracht', pricingMode: 'RETROGRADE', surchargePct: 0, isSplit: true },
  OPTIONS: { title: 'Optionen', pricingMode: 'RETROGRADE', surchargePct: 0, isSplit: false },
};

/** Ganze Mikro-Einheiten als String (bigint über JSON). */
export const microsStringSchema = z.string().regex(/^-?\d{1,20}$/, 'Betrag in Mikro-Einheiten (ganze Zahl) erwartet');

export const localizedTextSchema = z.object({
  de: z.string().max(4000),
  en: z.string().max(4000),
});
export type LocalizedText = z.infer<typeof localizedTextSchema>;

export const quoteCalcLineSchema = z.object({
  id: z.string().trim().min(1).max(64),
  category: z.string().trim().min(1).max(120),
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string().trim().max(64).nullable(),
  articleName: z.string().trim().max(300),
  unit: z.string().trim().max(20),
  /** EK je Einheit. */
  purchasePriceMicros: microsStringSchema,
  /** EK von Hand statt aus dem Artikelstamm (Schloss am EK). */
  isPurchasePriceOverridden: z.boolean(),
  /** Listen-VK des Artikels je Einheit (zählt nur retrograd). */
  listPriceMicros: microsStringSchema,
  quantityOverride: z.number().finite().min(-1_000_000).max(1_000_000).nullable(),
  unitPriceOverrideMicros: microsStringSchema.nullable(),
  offerTotalOverrideMicros: microsStringSchema.nullable(),
  /** Nur Optionen: je Becken (true) oder pauschal (false). */
  isPerBasin: z.boolean(),
  showInOffer: z.boolean(),
  showInSpecs: z.boolean(),
  /** Text der Position im Angebot (DE/EN), Schnappschuss aus dem Artikel; null = Artikelname. */
  offerText: localizedTextSchema.nullable().optional(),
  /** Titel der Spezifikationskarte aus dem Artikel; null = Titel der Kategorie. */
  specTitle: localizedTextSchema.nullable().optional(),
  /** Anlage der Position; null = ganzes Angebot. Fehlt bei alten Kalkulationen (→ `normalizeQuoteCalc`). */
  plantId: z.string().trim().min(1).max(64).nullable().optional(),
  /** Datenblatt-Merkmale (Bezeichnung/Wert je Sprache), Schnappschuss aus dem Artikel. */
  specs: z
    .object({
      de: z.array(z.object({ label: z.string().max(1000), value: z.string().max(2000) })).max(60),
      en: z.array(z.object({ label: z.string().max(1000), value: z.string().max(2000) })).max(60),
    })
    .nullable()
    .optional(),
});
export type QuoteCalcLine = z.infer<typeof quoteCalcLineSchema>;

export const quoteCalcGroupSchema = z.object({
  key: quoteCalcGroupKeySchema,
  title: z.string().trim().max(120),
  pricingMode: quoteCalcPricingModeSchema,
  surchargePct: z.number().finite().min(-100).max(1000),
  isSplit: z.boolean(),
  /** Je Anlage (PLANT) oder einmal fürs ganze Angebot (PROJECT); Optionen ignorieren das. */
  scope: z.enum(['PLANT', 'PROJECT']).optional(),
  lines: z.array(quoteCalcLineSchema).max(600),
});
export type QuoteCalcGroup = z.infer<typeof quoteCalcGroupSchema>;

/** Gruppen, die bei neuen und alten Kalkulationen fürs ganze Angebot gelten. */
export const QUOTE_CALC_PROJECT_GROUPS: readonly QuoteCalcGroupKey[] = ['ASSEMBLY', 'DOCUMENTATION', 'FREIGHT'];
export const defaultGroupScope = (key: QuoteCalcGroupKey): QuoteCalcGroupScope => (QUOTE_CALC_PROJECT_GROUPS.includes(key) ? 'PROJECT' : 'PLANT');

/** Eine Anlage des Angebots (z. B. „2 Stk. 4-Wellen-Räumer"): eigener Typ, eigene Maße, eigene Becken. */
export const quoteCalcPlantSchema = z.object({
  id: z.string().trim().min(1).max(64),
  /** Intern, z. B. „Anlage 1" oder „Vorklärung". */
  name: z.string().trim().min(1).max(120),
  /** Untertitel im Angebot je Sprache, z. B. „2 pcs. 4-shaft scrapers". */
  subtitle: localizedTextSchema,
  scraperType: scraperTypeSchema,
  basinCount: z.number().int().min(1).max(999),
  lengthM: z.number().finite().min(0).max(1000),
  widthM: z.number().finite().min(0).max(100),
  heightM: z.number().finite().min(0).max(100),
  waterLevelM: z.number().finite().min(0).max(100),
  flightSpacingM: z.number().finite().min(0).max(100),
  scrapingSpeedCmS: z.number().finite().min(0).max(1000),
  flightCountOverride: z.number().int().min(0).max(10_000).nullable(),
  flightLengthOverrideM: z.number().finite().min(0).max(100).nullable(),
  /** Preis der Anlage fest; null = aus dem Gesamtpreis verteilt. */
  priceLockMicros: microsStringSchema.nullable(),
});
export type QuoteCalcPlant = z.infer<typeof quoteCalcPlantSchema>;

const headerFields = {
  name: z.string().trim().min(1, 'Bezeichnung fehlt').max(200),
  subtitle: z.string().trim().max(200).nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().trim().max(200).nullable(),
  legalEntityId: z.string().uuid().nullable(),
  language: quoteCalcLanguageSchema,
  scraperType: scraperTypeSchema,
  basinCount: z.number().int().min(1).max(999),
  lengthM: z.number().finite().min(0).max(1000),
  widthM: z.number().finite().min(0).max(100),
  heightM: z.number().finite().min(0).max(100),
  waterLevelM: z.number().finite().min(0).max(100),
  flightSpacingM: z.number().finite().min(0).max(100),
  scrapingSpeedCmS: z.number().finite().min(0).max(1000),
  flightCountOverride: z.number().int().min(0).max(10_000).nullable(),
  flightLengthOverrideM: z.number().finite().min(0).max(100).nullable(),
  materialOverheadPct: z.number().finite().min(-100).max(1000),
  salesOverheadPct: z.number().finite().min(-100).max(1000),
  commissionPct: z.number().finite().min(0).max(100),
  finalPriceMicros: microsStringSchema.nullable(),
  validUntil: z.string().datetime().nullable(),
  notes: z.string().trim().max(4000).nullable(),
};

export const createQuoteCalculationSchema = z.object({
  /** Leer = nächste freie Nummer (JJMMTT + laufende Nummer ab 30, wie in der alten App). */
  number: z.string().trim().regex(/^[A-Za-z0-9-]{1,32}$/, 'Nummer: nur Buchstaben, Ziffern und „-"').optional(),
  name: headerFields.name,
  subtitle: headerFields.subtitle.optional(),
  customerId: headerFields.customerId.optional(),
  customerName: headerFields.customerName.optional(),
  legalEntityId: headerFields.legalEntityId.optional(),
  language: headerFields.language.default('DE'),
  scraperType: headerFields.scraperType,
  basinCount: headerFields.basinCount,
  lengthM: headerFields.lengthM,
  widthM: headerFields.widthM,
  heightM: headerFields.heightM,
  waterLevelM: headerFields.waterLevelM,
  flightSpacingM: headerFields.flightSpacingM,
  scrapingSpeedCmS: headerFields.scrapingSpeedCmS.default(1),
  notes: headerFields.notes.optional(),
  /** Aus einer Offer-Checkliste: Optionen als Positionen, Lieferumfang blendet Gruppen aus. */
  fromChecklist: createFromOfferChecklistSchema.optional(),
});
export type CreateQuoteCalculationInput = z.infer<typeof createQuoteCalculationSchema>;

export const updateQuoteCalculationSchema = z
  .object({
    ...headerFields,
    groups: z.array(quoteCalcGroupSchema).max(QUOTE_CALC_GROUP_KEYS.length),
    plants: z.array(quoteCalcPlantSchema).min(1).max(30),
    /** Stand, den der Client gesehen hat — weicht er ab, gewinnt nicht still der Letzte (409). */
    expectedUpdatedAt: z.string().datetime(),
  })
  .partial()
  .required({ expectedUpdatedAt: true });
export type UpdateQuoteCalculationInput = z.infer<typeof updateQuoteCalculationSchema>;

/** Kalkulation als neue Kalkulation kopieren (neue Nummer, Revision 0, Entwurf) — z. B. für eine ähnliche Anlage. */
export const copyQuoteCalculationSchema = z.object({
  name: headerFields.name,
  customerId: headerFields.customerId.optional(),
  customerName: headerFields.customerName.optional(),
  language: headerFields.language.optional(),
});
export type CopyQuoteCalculationInput = z.infer<typeof copyQuoteCalculationSchema>;

export const setQuoteCalcStatusSchema = z.object({
  status: quoteCalcStatusSchema,
  /** Nur bei „Verloren": warum (optional, z. B. Preis, Wettbewerber, Projekt gestoppt). */
  lostReason: z.string().trim().max(500).nullable().optional(),
});
export type SetQuoteCalcStatusInput = z.infer<typeof setQuoteCalcStatusSchema>;

const headerResponseFields = {
  id: z.string().uuid(),
  number: z.string(),
  revision: z.number().int(),
  /** „24112630-Rev.1" bzw. nur die Nummer bei Revision 0. */
  displayNumber: z.string(),
  status: quoteCalcStatusSchema,
  name: z.string(),
  subtitle: z.string().nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  legalEntityId: z.string().uuid().nullable(),
  language: quoteCalcLanguageSchema,
  scraperType: scraperTypeSchema,
  basinCount: z.number().int(),
  lengthM: z.number(),
  widthM: z.number(),
  heightM: z.number(),
  waterLevelM: z.number(),
  flightSpacingM: z.number(),
  scrapingSpeedCmS: z.number(),
  finalPriceMicros: microsStringSchema.nullable(),
  /** Kalkuliertes VK/Angebot ohne Optionen, alle Becken (Schnappschuss beim Speichern). */
  calculatedOfferMicros: microsStringSchema.nullable(),
  /** Ergebnis auf SK in % vom Angebotspreis (Schnappschuss beim Speichern). */
  resultPct: z.number().nullable(),
  validUntil: z.string().datetime().nullable(),
  sentAt: z.string().datetime().nullable(),
  decidedAt: z.string().datetime().nullable(),
  lostReason: z.string().nullable(),
  /** Übergeben an Verkauf/Projekt — dann gesperrt. */
  handedOverAt: z.string().datetime().nullable(),
  handoverSalesDocumentId: z.string().uuid().nullable(),
  handoverProjectId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
};

export const quoteCalculationListItemSchema = z.object({
  ...headerResponseFields,
  /** Anzahl Anlagen; Typ und Maße der Liste sind die der ersten Anlage. */
  plantCount: z.number().int(),
});
export type QuoteCalculationListItem = z.infer<typeof quoteCalculationListItemSchema>;

export const quoteCalculationResponseSchema = z.object({
  ...headerResponseFields,
  flightCountOverride: z.number().int().nullable(),
  flightLengthOverrideM: z.number().nullable(),
  materialOverheadPct: z.number(),
  salesOverheadPct: z.number(),
  commissionPct: z.number(),
  notes: z.string().nullable(),
  groups: z.array(quoteCalcGroupSchema),
  /** Anlagen des Angebots (mindestens eine; alte Kalkulationen: eine aus den Kopfmaßen). */
  plants: z.array(quoteCalcPlantSchema),
  /** Alle Revisionen derselben Nummer, neueste zuerst. */
  revisions: z.array(z.object({ id: z.string().uuid(), revision: z.number().int(), displayNumber: z.string(), createdAt: z.string().datetime(), finalPriceMicros: microsStringSchema.nullable() })),
});
export type QuoteCalculationResponse = z.infer<typeof quoteCalculationResponseSchema>;

export const quoteCalculationListQuerySchema = listQuerySchema.extend({
  /** kommagetrennt, z. B. „DRAFT,SENT" */
  status: z.string().trim().max(60).optional(),
  scraperType: scraperTypeSchema.optional(),
});
export type QuoteCalculationListQuery = z.infer<typeof quoteCalculationListQuerySchema>;

export const quoteCalculationStatsSchema = z.object({
  total: z.number().int(),
  byStatus: z.record(quoteCalcStatusSchema, z.object({ count: z.number().int(), finalPriceMicros: microsStringSchema })),
});
export type QuoteCalculationStats = z.infer<typeof quoteCalculationStatsSchema>;

// ---------- Vorlagen ----------

export const quoteCalcFormulaRowSchema = z.object({
  category: z.string().trim().min(1).max(120),
  formulas: z.object({
    FOUR_SHAFT: z.string().max(500).nullable(),
    THREE_SHAFT: z.string().max(500).nullable(),
    TWO_SHAFT_BOTTOM: z.string().max(500).nullable(),
    TWO_SHAFT_SURFACE: z.string().max(500).nullable(),
  }),
  specTitle: localizedTextSchema,
  specLabels: z.object({ de: z.array(z.string().max(120)).max(10), en: z.array(z.string().max(120)).max(10) }),
  sortOrder: z.number().int().min(0).max(100_000),
});
export type QuoteCalcFormulaRow = z.infer<typeof quoteCalcFormulaRowSchema>;

export const quoteCalcStandardPositionSchema = z.object({
  group: quoteCalcGroupKeySchema,
  category: z.string().trim().min(1).max(120),
  articleId: z.string().uuid().nullable(),
  sortOrder: z.number().int().min(0).max(100_000),
  showInOffer: z.boolean(),
  showInSpecs: z.boolean(),
});
export type QuoteCalcStandardPosition = z.infer<typeof quoteCalcStandardPositionSchema>;

export const quoteCalcGroupTextSchema = z.object({
  group: quoteCalcGroupKeySchema,
  /** null = gilt für alle Räumertypen; ein Text mit Typ schlägt den allgemeinen. */
  scraperType: scraperTypeSchema.nullable(),
  isLumpSum: z.boolean(),
  title: localizedTextSchema,
  text: localizedTextSchema,
});
export type QuoteCalcGroupText = z.infer<typeof quoteCalcGroupTextSchema>;

/** Bereiche, in denen ein Textbaustein im Angebots-Designer angeboten wird. */
export const QUOTE_CALC_SNIPPET_AREAS = ['TERMS', 'GROUP', 'OPTION', 'GENERAL'] as const;
export type QuoteCalcSnippetArea = (typeof QUOTE_CALC_SNIPPET_AREAS)[number];
export const QUOTE_CALC_SNIPPET_AREA_LABELS: Record<QuoteCalcSnippetArea, string> = {
  TERMS: 'Konditionen',
  GROUP: 'Gruppentext',
  OPTION: 'Optionsbeschreibung',
  GENERAL: 'Überall',
};
/** Konditionen mit Freitext (wie `OFFER_TERM_KEYS` ohne die Gültigkeit). */
export const QUOTE_CALC_SNIPPET_TERM_KEYS = ['priceBasis', 'delivery', 'deliveryTime', 'payment', 'warranty', 'other'] as const;

/** Wiederverwendbarer Text DE/EN für das Angebot (Konditionen, Pauschaltexte, Optionen). */
export const quoteCalcTextSnippetSchema = z.object({
  name: z.string().trim().min(1, 'Name fehlt').max(120),
  area: z.enum(QUOTE_CALC_SNIPPET_AREAS),
  /** Nur Konditionen: für welche Kondition; null = für alle. */
  termKey: z.enum(QUOTE_CALC_SNIPPET_TERM_KEYS).nullable(),
  text: localizedTextSchema,
});
export type QuoteCalcTextSnippet = z.infer<typeof quoteCalcTextSnippetSchema>;

export const quoteCalcTemplatesSchema = z.object({
  formulas: z.array(quoteCalcFormulaRowSchema).max(500),
  standardPositions: z.array(quoteCalcStandardPositionSchema).max(500),
  groupTexts: z.array(quoteCalcGroupTextSchema).max(200),
  textSnippets: z.array(quoteCalcTextSnippetSchema).max(300),
});
export type QuoteCalcTemplates = z.infer<typeof quoteCalcTemplatesSchema>;

export const quoteCalcStandardPositionResponseSchema = quoteCalcStandardPositionSchema.extend({
  articleNumber: z.string().nullable(),
  articleName: z.string().nullable(),
});
export const quoteCalcTemplatesResponseSchema = quoteCalcTemplatesSchema.extend({
  standardPositions: z.array(quoteCalcStandardPositionResponseSchema),
});
export type QuoteCalcTemplatesResponse = z.infer<typeof quoteCalcTemplatesResponseSchema>;

// ---------- Brücke zum Rechenkern ----------

/** Formeln einer Vorlage für einen Räumertyp, wie der Rechenkern sie erwartet. */
export function formulasForScraperType(rows: readonly QuoteCalcFormulaRow[], scraperType: ScraperType): QuoteCalcFormulas {
  const formulas: Record<string, string | null> = {};
  if (!(FORMULA_SCRAPER_TYPES as readonly string[]).includes(scraperType)) return formulas; // Sonderanlage: keine Formeln
  for (const row of rows) formulas[row.category] = row.formulas[scraperType as FormulaScraperType];
  return formulas;
}

const big = (value: string | null): bigint | null => (value === null ? null : BigInt(value));

type CalcHeader = Pick<
  QuoteCalculationResponse,
  | 'basinCount' | 'lengthM' | 'widthM' | 'heightM' | 'waterLevelM' | 'flightSpacingM' | 'scrapingSpeedCmS'
  | 'flightCountOverride' | 'flightLengthOverrideM' | 'scraperType' | 'subtitle'
>;

/** Erste Anlage aus den Kopfmaßen einer Kalkulation von vor den Anlagen. */
export function plantFromHeader(calc: CalcHeader, id = 'p1'): QuoteCalcPlant {
  return {
    id,
    name: 'Anlage 1',
    subtitle: { de: calc.subtitle ?? '', en: calc.subtitle ?? '' },
    scraperType: calc.scraperType,
    basinCount: calc.basinCount,
    lengthM: calc.lengthM,
    widthM: calc.widthM,
    heightM: calc.heightM,
    waterLevelM: calc.waterLevelM,
    flightSpacingM: calc.flightSpacingM,
    scrapingSpeedCmS: calc.scrapingSpeedCmS,
    flightCountOverride: calc.flightCountOverride,
    flightLengthOverrideM: calc.flightLengthOverrideM,
    priceLockMicros: null,
  };
}

/**
 * Alte und neue Kalkulationen auf eine Form bringen: mindestens eine Anlage, jede
 * Gruppe mit `scope`, jede Position mit `plantId` (Gruppen je Anlage → erste Anlage,
 * Gruppen fürs Angebot → null, Optionen: je Becken → erste Anlage, pauschal → Angebot).
 */
export function normalizeQuoteCalc<T extends CalcHeader & { groups: QuoteCalcGroup[]; plants?: QuoteCalcPlant[] | null }>(calc: T): T & { plants: QuoteCalcPlant[] } {
  const plants = calc.plants?.length ? calc.plants : [plantFromHeader(calc)];
  const ids = new Set(plants.map((plant) => plant.id));
  const first = plants[0]!.id;
  const groups = calc.groups.map((group) => {
    const scope = group.scope ?? defaultGroupScope(group.key);
    const lines = group.lines.map((line) => {
      let plantId = line.plantId;
      if (group.key === 'OPTIONS') plantId = plantId === undefined ? (line.isPerBasin ? first : null) : plantId !== null && !ids.has(plantId) ? first : plantId;
      else if (scope === 'PLANT') plantId = plantId && ids.has(plantId) ? plantId : first;
      else plantId = null;
      return { ...line, plantId };
    });
    return { ...group, scope, lines };
  });
  return { ...calc, plants, groups };
}


/** Kalkulation (API-Form) → Eingabe des Rechenkerns; `formulasFor` liefert die Formeln je Räumertyp. */
export function toQuoteCalcInput(
  calc: Pick<
    QuoteCalculationResponse,
    | 'basinCount' | 'lengthM' | 'widthM' | 'heightM' | 'waterLevelM' | 'flightSpacingM' | 'scrapingSpeedCmS'
    | 'flightCountOverride' | 'flightLengthOverrideM' | 'materialOverheadPct' | 'salesOverheadPct'
    | 'commissionPct' | 'finalPriceMicros' | 'groups' | 'scraperType' | 'subtitle'
  > & { plants?: QuoteCalcPlant[] | null },
  formulasFor: (scraperType: ScraperType) => QuoteCalcFormulas,
): QuoteCalcInput {
  const normal = normalizeQuoteCalc(calc);
  const plants = normal.plants.map((plant) => ({
    id: plant.id,
    dimensions: {
      basinCount: plant.basinCount,
      lengthM: plant.lengthM,
      widthM: plant.widthM,
      heightM: plant.heightM,
      waterLevelM: plant.waterLevelM,
      flightSpacingM: plant.flightSpacingM,
    },
    formulas: formulasFor(plant.scraperType),
    flightCountOverride: plant.flightCountOverride,
    flightLengthOverrideM: plant.flightLengthOverrideM,
    priceLockMicros: big(plant.priceLockMicros),
  }));
  const first = plants[0]!;
  return {
    dimensions: first.dimensions,
    formulas: first.formulas,
    flightCountOverride: first.flightCountOverride,
    flightLengthOverrideM: first.flightLengthOverrideM,
    plants,
    materialOverheadPct: calc.materialOverheadPct,
    salesOverheadPct: calc.salesOverheadPct,
    commissionPct: calc.commissionPct,
    finalPriceMicros: big(calc.finalPriceMicros),
    groups: normal.groups.map((group) => ({
      key: group.key,
      pricingMode: group.pricingMode,
      surchargePct: group.surchargePct,
      isSplit: group.isSplit,
      isOptions: group.key === 'OPTIONS',
      scope: group.scope,
      lines: group.lines.map((line) => ({
        id: line.id,
        category: line.category,
        purchasePriceMicros: BigInt(line.purchasePriceMicros),
        listPriceMicros: BigInt(line.listPriceMicros),
        quantityOverride: line.quantityOverride,
        unitPriceOverrideMicros: big(line.unitPriceOverrideMicros),
        offerTotalOverrideMicros: big(line.offerTotalOverrideMicros),
        isPerBasin: line.isPerBasin,
        plantId: line.plantId,
      })),
    })),
  };
}

/** Rechnet eine Kalkulation in API-Form mit den Formeln des Räumertyps jeder Anlage. */
export function computeQuoteCalculationFor(
  calc: Parameters<typeof toQuoteCalcInput>[0],
  formulaRows: readonly QuoteCalcFormulaRow[],
): QuoteCalcResult {
  return computeQuoteCalculation(toQuoteCalcInput(calc, (scraperType) => formulasForScraperType(formulaRows, scraperType)));
}

/**
 * Angebotstext und Spezifikation eines Artikels als Schnappschuss an der Position
 * (beim Hinzufügen, Tauschen oder Nachziehen aus dem Artikelstamm). Leeres bleibt null.
 */
export function lineOfferFieldsFromArticle(spec: ArticleOfferSpec): Pick<QuoteCalcLine, 'offerText' | 'specTitle' | 'specs'> {
  const text = (de: string, en: string) => (de.trim() || en.trim() ? { de: de.trim(), en: en.trim() } : null);
  return {
    offerText: text(spec.offerTextDe, spec.offerTextEn),
    specTitle: text(spec.specTitleDe, spec.specTitleEn),
    specs: spec.specs.length ? specRowsToLocalized(spec.specs) : null,
  };
}

// ---------- Spezifikationen im Artikelstamm (Arbeitsliste) ----------

export const articleSpecWorklistItemSchema = z.object({
  articleId: z.string().uuid(),
  number: z.string(),
  name: z.string(),
  unit: z.string(),
  /** Häufigste Kategorie der Positionen mit diesem Artikel (für Titel und Vorlage). */
  category: z.string(),
  /** Positionen in allen Kalkulationen. */
  uses: z.number().int(),
  /** Kalkulationen im Entwurf oder gesendet, die den Artikel enthalten. */
  openCalculations: z.number().int(),
  offerSpec: articleOfferSpecSchema,
  status: z.enum(['COMPLETE', 'MISSING_EN', 'MESSY', 'EMPTY']),
  /** Eine Kalkulation hat Text/Merkmale dieses Artikels (z. B. aus base44), der Stamm noch nicht. */
  canAdoptFromCalculation: z.boolean(),
});
export type ArticleSpecWorklistItem = z.infer<typeof articleSpecWorklistItemSchema>;

export const saveArticleOfferSpecSchema = z.object({
  spec: articleOfferSpecSchema,
  /** Positionen in offenen Kalkulationen (Entwurf, Gesendet) mit dem neuen Stand nachziehen. */
  syncOpenCalculations: z.boolean(),
});
export type SaveArticleOfferSpecInput = z.infer<typeof saveArticleOfferSpecSchema>;

export const saveArticleOfferSpecResultSchema = z.object({
  item: articleSpecWorklistItemSchema.nullable(),
  syncedCalculations: z.number().int(),
  syncedLines: z.number().int(),
});
export type SaveArticleOfferSpecResult = z.infer<typeof saveArticleOfferSpecResultSchema>;
